import React from 'react'
import { PlusIcon } from '@phosphor-icons/react'
import i18next from 'i18next'

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'dark' | 'text' | 'link' | 'default' | 'outline' | 'chip' | 'danger' | 'smallButton'
  size?: 'tiny' | 'small' | 'medium' | 'large' | 'controlbar'
  disabled?: boolean
  loading?: boolean
  active?: boolean
  children: React.ReactNode
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  className?: string
  icon?: 'plus' | React.ReactNode
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  active = false,
  children,
  onClick,
  type = 'button',
  className = '',
  icon
}) => {
  const baseClasses = 'box-border flex gap-2.5 items-center justify-center !font-nunito font-bold text-center whitespace-nowrap !leading-none cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed'

  const variantClasses = {
    primary: 'bg-brand-green text-primary rounded-full border border-brand-green hover:bg-brand-green-400',
    secondary: 'bg-transparent text-disabled rounded-full border border-text-disabled hover:border-text-muted hover:text-muted',
    dark: 'bg-brand-black text-brand-white rounded-full border border-brand-black hover:bg-text-primary disabled:bg-text-disabled disabled:border-text-disabled',
    text: 'bg-transparent text-blue border-none hover:underline',
    link: 'bg-transparent text-blue border-none font-semibold justify-end gap-1 hover:opacity-80',
    default: 'bg-transparent text-brand-black rounded-full border-none hover:bg-brand-white/50',
    outline: 'bg-brand-white text-primary rounded-full border border-text-primary hover:bg-system',
    danger: 'bg-tertiary-error text-white rounded-full border border-tertiary-error hover:bg-red-600',
    chip: active
      ? 'px-4 py-2 bg-brand-green rounded-[100px] outline outline-1 outline-offset-[-1px] outline-brand-green inline-flex justify-center items-center text-primary !text-3xs !leading-4 hover:bg-brand-green-400 cursor-pointer'
      : 'px-4 py-2 bg-white rounded-pill outline outline-1 -outline-offset-1 outline-emerald-500 inline-flex justify-center items-center text-primary !text-3xs !leading-4 hover:bg-gray-50 cursor-pointer',
    smallButton:
      'bg-brand-green text-primary rounded-full border border-brand-green hover:bg-brand-green-400 box-border flex cursor-pointer items-center justify-center gap-2.5 px-4 py-1.5 !font-nunito text-center text-xs font-bold whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50',
  }

  const sizeClasses = {
    tiny: '!text-3xs',
    small: 'px-6 py-2 !text-2-5xs h-6',
    medium: 'px-9 py-[13px] !text-1-5xs h-7',
    large: 'px-[36px] py-[13px] !text-1-5xs font-bold',
  }

  // For link variant, use specific sizing
  const linkSizeClasses = {
    tiny: '!text-3xs font-medium !leading-4',
    small: '!text-2xs !leading-[20px]',
    medium: '!text-xs !leading-[24px]',
    large: '!text-sm !leading-[28px]',
  }

  const renderIcon = () => {
    if (!icon) return null

    if (icon === 'plus') {
      return (
        <div className="shrink-0 size-[13.675px]">
          <PlusIcon className="w-full h-full" />
        </div>
      )
    }

    return icon
  }

  const classes = variant === 'link'
    ? `${baseClasses} ${variantClasses[variant]} ${linkSizeClasses[size]} ${className}`
    : variant === 'chip' || variant === 'smallButton'
      ? `${variantClasses[variant]} ${className}`
      : `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={classes}
    >
      {renderIcon()}
      {loading ? `${i18next.t('loading')}...` : children}
    </button>
  )
}

export default Button